import axiosHandler from '@/utils/axios-handler';
import axios from 'axios';
import { getUserData } from '@/helpers/local-storage';
import { academyApiUrl } from '@/lib/constants/api';

const DEFAULT_CONFIG = {
  engine_key: process.env.NEXT_PUBLIC_ENGINE_KEY,
  spelling: 'always',
  per_page: 9,
};

interface UserDataItem {
  entryUid: string;
  [key: string]: any;
}

let userData: UserDataItem[] = [];

/**
 * Fetch user courses and lessons for search result enrichment (Academy 1.0 behavior).
 * Uses 1× courses + 1× users/lessons.
 */
const getUserCoursesAndLessons = async (): Promise<void> => {
  const userFromStorage = getUserData();
  if (!userFromStorage) {
    userData = [];
    return;
  }
  try {
    // Independent calls — issued together. Each now goes through this app's own server
    // (same-origin proxy), so awaiting them in sequence would pay that hop twice.
    const [coursesResponse, lessonsResponse] = await Promise.all([
      axiosHandler({
        method: 'GET',
        url: academyApiUrl('/v1/users/courses'),
        withCredentials: true,
      }),
      axiosHandler({
        method: 'GET',
        url: academyApiUrl('/v1/users/lessons?getCourseEntryUid=true'),
        withCredentials: true,
      }),
    ]);
    const courses = (coursesResponse as any)?.data?.courses ?? [];
    const res = (lessonsResponse as any)?.data;
    const lessons = Array.isArray(res) ? res : res?.lessons ?? res?.data ?? [];

    if (courses.length > 0 || lessons.length > 0) {
      userData = [...courses, ...lessons];
    } else {
      userData = [];
    }
  } catch {
    userData = [];
  }
};

interface SearchOptions {
  q: string;
  filters?: any[];
  page?: number;
  perPage?: number;
}

interface SearchResponse {
  records: {
    page: Array<{
      entry_uid?: string;
      [key: string]: any;
    }>;
  };
  info: {
    page: {
      spelling_suggestion?: {
        text: string;
      };
      [key: string]: any;
    };
  };
  [key: string]: any;
}

export const search = async (
  q: string,
  filters: any[] | string = [],
  page: number = 1,
  perPage: number = 9,
  options?: { excludePartnerSpecific?: boolean }
): Promise<SearchResponse | undefined> => {
  let availableFilters: string[] = [];
  const arrFilter =
    typeof filters === 'string'
      ? filters.length > 1
        ? filters.split(',')
        : [filters]
      : filters;

  if (arrFilter.length) {
    arrFilter.forEach((el) => {
      availableFilters.push(el);
    });
  }

  const excludePartnerSpecific = options?.excludePartnerSpecific ?? true;

  try {
    // Academy 1.0: enrich results with user_data (progress, etc.) when user is logged in
    if (getUserData()) {
      await getUserCoursesAndLessons();
    } else {
      userData = [];
    }

    const body: any = {
      ...DEFAULT_CONFIG,
      per_page: perPage,
      q: `${q.replace(/&/g, ' ')}`,
    };

    // Filters – Academy 1.0: filters.page.tags (content type) + is_partner_specific for non-partner users
    const pageFilters: Record<string, string[] | string> = {};
    if (availableFilters.length) {
      pageFilters.tags = availableFilters;
    }
    if (excludePartnerSpecific) {
      pageFilters.is_partner_specific = ['false'];
    }
    if (Object.keys(pageFilters).length > 0) {
      body['filters'] = { page: pageFilters };
    }

    // Pagination
    if (page) {
      body['page'] = page;
    }

    // Search Request (Swiftype)
    const result = await axios({
      method: 'POST',
      url: 'https://search-api.swiftype.com/api/v1/public/engines/search.json',
      headers: {
        'Content-Type': 'application/json',
      },
      data: body,
    });

    if (result.data.info?.page?.spelling_suggestion) {
      return search(
        result.data.info.page.spelling_suggestion.text,
        filters,
        page,
        perPage,
        options
      );
    }

    // Academy 1.0: attach user_data to each hit by entry_uid for progress/UI
    const userMap = new Map(
      userData.map((userItem) => [userItem.entryUid, userItem])
    );
    result.data.records.page.forEach((resultItem: any) => {
      if (userMap.has(resultItem.entry_uid)) {
        resultItem['user_data'] = userMap.get(resultItem.entry_uid);
      }
    });

    return result.data;
  } catch (error) {
    return undefined;
  }
};

